import React, { useState, useRef, useEffect } from "react";
import video from "./2021-03-22-homepage-video.mp4";
import Heading2 from "../Typography/Heading2";
import Heading3 from "../Typography/Heading3";
import Note from "../Typography/Note";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion, AnimatePresence } from "../../utils/framer-motion-custom";
import editorTheme from "../../../../../stylesheets/editor_css_config.json";
import debounce from "lodash/debounce";

const HomepageVideo = () => {
  const chapterCards = [
    {
      title: "Automatically add data",
      copy: "to try new ideas quickly",
      icon: "magic",
      timestamp: 0
    },
    {
      title: "Dynamic narratives",
      copy: "to guide your audience to insights",
      icon: "text",
      timestamp: 20
    },
    {
      title: "Built in design",
      copy: "so your data always looks professional",
      icon: "drafting-compass",
      timestamp: 52
    },
    {
      title: "Effortless sharing",
      copy: "so you can reach 10 or 10,000 people",
      icon: "link",
      timestamp: 66
    }
  ];

  const variants = {
    active: {
      left: "1rem",
      color: editorTheme.colors.accent,
      opacity: 1,
      backgroundColor: "transparent",
      display: "flex",
      x: 0,
      zIndex: 1,
      boxShadow: "none"
    },
    inactive: {
      left: 0,
      color: "#000",
      opacity: 0.5,
      backgroundColor: "",
      display: "flex",
      x: 0,
      zIndex: 1,
      boxShadow: ""
    }
  };

  const mobileVariants = {
    active: {
      zIndex: 1,
      opacity: 1,
      x: 0,
      color: editorTheme.colors.accent,
      display: "flex",
      left: 0,
      backgroundColor: "transparent",
      boxShadow: "none"
    },
    inactive: {
      opacity: 0,
      zIndex: 0,
      x: 1000,
      color: "#000",
      display: "none",
      left: 0,
      backgroundColor: "transparent",
      boxShadow: "none"
    }
  };

  const [isActive, setIsActive] = useState(0);
  const videoDiv = useRef(null);
  const endOfList = isActive + 1 >= chapterCards.length;
  const timeMath = endOfList
    ? (videoDiv.current.querySelector("video").duration -
        chapterCards[chapterCards.length - 1].timestamp) *
      1000
    : (chapterCards[isActive + 1].timestamp -
        chapterCards[isActive].timestamp) *
      1000;

  let currentTimeout;
  useEffect(() => {
    currentTimeout = setTimeout(() => {
      if (endOfList) {
        setIsActive(0);
      } else {
        setIsActive(isActive + 1);
      }
    }, timeMath);

    // Clear timeout on un-mount.
    return () => {
      clearTimeout(currentTimeout);
    };
  }, [isActive]);

  const [width, setWidth] = useState(window.innerWidth);
  window.addEventListener(
    "resize",
    debounce(() => {
      setWidth(window.innerWidth);
    }, 500)
  );
  return (
    <div className="w-100 mw8-and-half center flex justify-center flex-wrap">
      <div className="w-100 w-auto-l pr2-l">
        <div className="dn db-l">
          <Heading2 weight="fw7">See how Juicebox works</Heading2>
        </div>
        <div className="pt3 mb3 mb0-l pt4-and-half-l flex flex-column-l flex-nowrap items-center items-start-l h3 h-auto-l">
          <AnimatePresence initial={false}>
            {chapterCards.map((c, i) => (
              <motion.div
                key={i}
                className="flex items-center justify-center justify-start-l pv2 ph3 w-100 w-auto-l flex-shrink-0 flex-shrink-1-l absolute relative-l o-50 pointer hide-child hover-bg-white br2 ba b--transparent hover-b--black-05 shadow-05-hover"
                custom={i}
                animate={isActive == i ? "active" : "inactive"}
                exit="inactive"
                variants={width > 959.99 ? variants : mobileVariants}
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  ease: [1, 1.83, 0.49, 1.18],
                  duration: 0.4
                }}
                onClick={() => {
                  currentTimeout && clearTimeout(currentTimeout);
                  if (isActive != i) {
                    setIsActive(i);
                    videoDiv.current.querySelector("video").currentTime =
                      c.timestamp;
                  }
                }}
              >
                <div className="pr3">
                  <FontAwesomeIcon icon={["far", c.icon]} size="lg" />
                </div>
                <div>
                  <Heading3 inline={true}>{c.title}</Heading3>
                  <Note>{c.copy}</Note>
                </div>
                <div
                  className={
                    isActive == i ? "dn db-l pl3 o-1" : "o-50 child pl3 dn db-l"
                  }
                  style={{
                    transition: "all .4 cubic-bexier(1, 1.83, 0.49, 1.18)"
                  }}
                >
                  <FontAwesomeIcon size="lg" icon={["far", "arrow-right"]} />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      <div
        ref={videoDiv}
        className="w-100 w-50-l"
        dangerouslySetInnerHTML={{
          __html: `<video width="100%" autoplay muted loop playsinline>
        <source src="${video}" type="video/mp4" />
      </video>`
        }}
      />
    </div>
  );
};

export default HomepageVideo;
